import { LoadingButton } from '@mui/lab';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ResponsiveSideDialog } from 'common/components/ResponsiveSideDialog/ResponsiveSideDialog';
import { DialogComponentProps, useDialog } from 'common/hooks/useDialog';
import { ContentDispositionType } from 'common/hooks/useFiles';
import { useFilesDownload } from 'common/hooks/useFilesDownload';
import { useNotification } from 'common/hooks/useNotification';
import { AuthContext } from 'common/providers/AuthContextProvider/AuthContextProvider';
import {
  FilterOperator,
  ReportType,
  SortDirection,
  useCashFlowAdjustmentsQuery,
  useCreateReportMutation,
} from 'generated/graphql';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FintelligenceReportFinancialReserveDialog } from '../FintelligenceReportFinancialReserveDialog/FintelligenceReportFinancialReserveDialog';
import {
  AddCashFlowAdjustmentDialog,
  AddCashFlowAdjustmentDialogProps,
  CashFlowAdjustmentType,
} from './AddCashFlowAdjustmentDialog/AddCashFlowAdjustmentDialog';
import { CashFlowAdjustmentListItem } from './CashFlowAdjustmentListItem/CashFlowAdjustmentListItem';
import { styles } from './FintelligenceReportCashFlowAdjustmentDialog.styles';

const FintelligenceReportCashFlowAdjustmentDialogContent: React.FC<DialogComponentProps<void>> = ({ onResolve }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useContext(AuthContext);
  const notify = useNotification();
  const [loading, setLoading] = useState(false);

  const { data: incomeData } = useCashFlowAdjustmentsQuery({
    notifyOnNetworkStatusChange: true,
    skip: !isAuthenticated,
    variables: {
      filterInput: {
        arguments: [
          {
            filterCondition: {
              fieldPath: 'period.expectedAmount.amount',
              operator: FilterOperator.GreaterThan,
              values: ['0'],
            },
          },
        ],
      },
      sortInput: {
        by: [{ direction: SortDirection.Desc, fieldPath: 'period.expectedAmount.amount' }],
      },
    },
  });

  const { data: expensesData } = useCashFlowAdjustmentsQuery({
    notifyOnNetworkStatusChange: true,
    skip: !isAuthenticated,
    variables: {
      filterInput: {
        arguments: [
          {
            filterCondition: {
              fieldPath: 'period.expectedAmount.amount',
              operator: FilterOperator.LessThan,
              values: ['0'],
            },
          },
        ],
      },
      sortInput: {
        by: [{ direction: SortDirection.Asc, fieldPath: 'period.expectedAmount.amount' }],
      },
    },
  });

  const addCashFlowAdjustmentDialog = useDialog<AddCashFlowAdjustmentDialogProps, void>(AddCashFlowAdjustmentDialog, {
    instanceId: 'add-cash-flow-adjustment',
    navigable: true,
  });

  const handleAddIncome = async () => {
    await addCashFlowAdjustmentDialog.create({ type: CashFlowAdjustmentType.Income });
  };
  const handleAddExpense = async () => {
    await addCashFlowAdjustmentDialog.create({ type: CashFlowAdjustmentType.Expense });
  };

  const [createReport] = useCreateReportMutation({});

  const { downloadFile } = useFilesDownload();

  const handleSubmit = async () => {
    setLoading(true);

    const resp = await createReport({
      onError: () => {
        notify({ message: t('common:somethingWentWrong'), type: 'error' });
        setLoading(false);
      },
      refetchQueries: ['Reports'],
      variables: {
        reportType: ReportType.Fintelligence,
      },
    });
    if (resp.data?.createReport.file !== undefined) {
      setLoading(false);
      await downloadFile(resp.data?.createReport.file, ContentDispositionType.ATTACHMENT, window.open());
      onResolve();
    } else {
      notify({ message: t('common:somethingWentWrong'), type: 'error' });
      setLoading(false);
    }
  };

  const fintelligenceReportFinancialReserveDialog = useDialog<DialogComponentProps<void>, void>(
    FintelligenceReportFinancialReserveDialog,
    {
      instanceId: 'fintelligence-report-financial-reserve-dialog',
      navigable: true,
    },
  );

  const handleBack = async () => {
    await onResolve();
    fintelligenceReportFinancialReserveDialog.create({});
  };

  return (
    <Box>
      <Stack alignItems="center" spacing={1}>
        <Typography color="textSecondary" variant="bodyM">
          {t('fintelligenceReport.cashFlowAdjustmentDialog.step')}
        </Typography>
        <Typography textAlign="center" variant="headlineXL">
          {t('fintelligenceReport.cashFlowAdjustmentDialog.title')}
        </Typography>
        <Typography textAlign="center" variant="bodyL">
          {t('fintelligenceReport.cashFlowAdjustmentDialog.text')}
        </Typography>
      </Stack>

      <Box mt={5}>
        <Typography variant="headlineM">{t('fintelligenceReport.cashFlowAdjustmentDialog.income.subtitle')}</Typography>
      </Box>
      <Stack sx={styles.container}>
        {incomeData?.cashFlowAdjustments?.items.map((value, index) => (
          <CashFlowAdjustmentListItem key={index} cashFlowAdjustment={value} />
        ))}
        <Box onClick={handleAddIncome} sx={styles.buttonRow}>
          <Stack direction="row" flexGrow={1} justifyContent="space-between">
            <Typography color="blue.dark" variant="bodyL">
              {t('fintelligenceReport.cashFlowAdjustmentDialog.income.add')}
            </Typography>
          </Stack>
        </Box>
      </Stack>

      <Box mt={5}>
        <Typography variant="headlineM">
          {t('fintelligenceReport.cashFlowAdjustmentDialog.expenses.subtitle')}
        </Typography>
      </Box>
      <Stack sx={styles.container}>
        {expensesData?.cashFlowAdjustments?.items.map((value, index) => (
          <CashFlowAdjustmentListItem key={index} cashFlowAdjustment={value} />
        ))}
        <Box onClick={handleAddExpense} sx={styles.buttonRow}>
          <Stack direction="row" flexGrow={1} justifyContent="space-between">
            <Typography color="blue.dark" variant="bodyL">
              {t('fintelligenceReport.cashFlowAdjustmentDialog.expenses.add')}
            </Typography>
          </Stack>
        </Box>
      </Stack>

      <Stack mt={5} spacing={1}>
        <LoadingButton color="primary" fullWidth loading={loading} onClick={handleSubmit} size="large">
          <Typography color="white" variant="bodyL">
            {t('fintelligenceReport.cashFlowAdjustmentDialog.submit')}
          </Typography>
        </LoadingButton>
        <Button color="tertiaryButton" fullWidth onClick={handleBack} size="large">
          <Typography color="white" variant="bodyL">
            {t('common:back')}
          </Typography>
        </Button>
      </Stack>
    </Box>
  );
};

export const FintelligenceReportCashFlowAdjustmentDialog: React.FC<DialogComponentProps<void>> = (props) => {
  return (
    <ResponsiveSideDialog dialogId={props.instanceId} isOpen={props.isOpen} onClose={props.onResolve}>
      <FintelligenceReportCashFlowAdjustmentDialogContent {...props} />
    </ResponsiveSideDialog>
  );
};
